/**
 * Flota — nowy pulpit (`fleet.web.board.*`), doklejony w `extra.ts`. Stare klucze `fleet.web.dash.*` zostają (filtry, kafelki kolejek, sekcje szczegółów).
 * `fleetBoardEn` jest typowany względem `fleetBoardPl` — brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetBoardPl = {
  board: {
    hero: { vehicles: 'Pojazdy', active: 'Aktywne', inService: 'W serwisie', onRoute: 'W trasie', alerts: 'Alerty', critical: 'Krytyczne' },
    actions: { vehicles: 'Pojazdy', logbooks: 'Karty do weryfikacji', incidents: 'Zgłoszenia', analytics: 'Analityka' },
    insight: {
      criticalAlerts: '{count} krytycznych alertów czeka na reakcję.',
      overdueDue: '{count} terminów przeglądu lub ubezpieczenia jest po czasie.',
      seriousIncidents: '{count} poważnych zgłoszeń wymaga uwagi.',
      leaseExceeded: '{count} umów leasingowych przekroczyło limit km.',
      dueSoon: '{count} terminów przypada w ciągu 7 dni.',
      pendingReview: '{count} pozycji czeka na weryfikację.',
      allGood: 'Brak pilnych spraw — flota w porządku.',
      action: { criticalAlerts: 'Pokaż alerty', overdueDue: 'Zobacz terminy', seriousIncidents: 'Zgłoszenia', leaseExceeded: 'Zobacz limity', dueSoon: 'Zobacz terminy', pendingReview: 'Do zrobienia' },
    },
    kpi: {
      km: 'Przebieg',
      cost: 'Koszty eksploatacji',
      costPerKm: 'Koszt na km',
      review: 'Do weryfikacji',
      alerts: 'Otwarte alerty',
      monthHint: '{month}',
      reviewHint: 'karty {logbooks} · tankowania {fuel} · koszty {expenses}',
      alertsHint: '{critical} krytycznych',
      alertsHintNone: 'brak krytycznych',
      vsPrev: 'vs poprzedni miesiąc',
      noTrend: 'za mało danych o trendzie',
    },
    queue: { title: 'Do zrobienia', description: 'Kolejki do weryfikacji i problemy wymagające reakcji. Kliknij, aby przejść do listy.' },
    health: {
      title: 'Zdrowie floty',
      description: 'Wskaźnik 0–100 z terminów, zgłoszeń, limitów leasingu i brakujących kart.',
      good: 'w dobrym stanie',
      attention: 'do uwagi',
      critical: 'krytycznych',
      attentionList: 'Pojazdy wymagające uwagi',
      empty: 'Żaden pojazd nie wymaga uwagi.',
      noVehicles: 'Brak pojazdów aktywnych lub w serwisie — wskaźnik nie jest liczony.',
      rule: 'Wskaźnik jest orientacyjny: liczony z sygnałów widocznych na pulpicie (zgłoszenia — do 5 ostatnich). Pojazd bez sygnałów = 100.',
      more: 'i {n} więcej',
    },
    today: {
      title: 'Pojazdy dziś',
      description: 'Stan z otwartych kart drogowych i statusu pojazdu.',
      all: 'Wszystkie',
      filter: 'Stan pojazdu',
      since: 'od {time}',
      planned: 'plan {km}',
      odometer: 'licznik {km}',
      noDriver: 'brak kierowcy',
      defaultDriver: 'domyślny kierowca',
      pendingDecision: 'czeka na decyzję',
      showAll: 'Pokaż wszystkie ({n})',
      showLess: 'Pokaż mniej',
      empty: 'Brak pojazdów dla wybranych filtrów.',
      emptyState: 'Żaden pojazd nie ma takiego stanu.',
      vehicles: 'Lista pojazdów',
      barAria: 'W trasie {a}, w serwisie {b}, postój {c}',
    },
    leases: {
      title: 'Limity leasingu',
      description: 'Wykorzystanie limitu km i prognoza na koniec umowy — najbardziej obciążone umowy.',
      remaining: 'zostało {km}',
      exceeded: 'przekroczono o {km}',
      forecast: 'prognoza {percent}',
      indicative: 'orientacyjnie',
      daysLeft: '{n} dni do końca umowy',
      empty: 'Brak aktywnych umów leasingowych.',
      all: 'Wszystkie umowy',
      limitOf: 'z {km}',
    },
    due: {
      title: 'Oś terminów',
      description: 'Przeglądy i ubezpieczenia w ciągu {days} dni (tygodniami).',
      overdue: 'Po terminie',
      week: 'dni {from}–{to}',
      weekNow: 'ten tydzień',
      empty: 'Brak terminów w tym okresie.',
      emptyBucket: 'Brak terminów w wybranym tygodniu.',
      showAll: 'Pokaż wszystkie',
      selected: 'Wybrano: {label}',
      barAria: '{label}: {n} terminów',
    },
    feed: {
      title: 'Alerty',
      description: 'Najważniejsze otwarte alerty floty.',
      empty: 'Brak otwartych alertów — flota w porządku.',
      all: 'Wszystkie alerty ({n})',
      go: 'Przejdź',
      ack: 'Potwierdź',
    },
    trend: {
      title: 'Koszty i przebieg',
      description: 'Ostatnie pełne miesiące (bez bieżącego, niepełnego).',
      metric: { km: 'Przebieg', cost: 'Koszty', costPerKm: 'Koszt/km' },
      aria: '{metric} — {from} do {to}',
      latest: 'Ostatni miesiąc: {value}',
      empty: 'Za mało danych do wykresu.',
      link: 'Pełna analityka',
    },
    details: 'Szczegóły',
  },
};

export const fleetBoardEn: Shape<typeof fleetBoardPl> = {
  board: {
    hero: { vehicles: 'Vehicles', active: 'Active', inService: 'In service', onRoute: 'On route', alerts: 'Alerts', critical: 'Critical' },
    actions: { vehicles: 'Vehicles', logbooks: 'Logbooks to review', incidents: 'Incidents', analytics: 'Analytics' },
    insight: {
      criticalAlerts: '{count} critical alerts need a response.',
      overdueDue: '{count} inspection or insurance deadlines are overdue.',
      seriousIncidents: '{count} serious incidents need attention.',
      leaseExceeded: '{count} lease contracts exceeded the km limit.',
      dueSoon: '{count} deadlines fall within 7 days.',
      pendingReview: '{count} items are waiting for review.',
      allGood: 'Nothing urgent — the fleet is in good shape.',
      action: { criticalAlerts: 'Show alerts', overdueDue: 'See deadlines', seriousIncidents: 'Incidents', leaseExceeded: 'See limits', dueSoon: 'See deadlines', pendingReview: 'To do' },
    },
    kpi: {
      km: 'Mileage',
      cost: 'Operating costs',
      costPerKm: 'Cost per km',
      review: 'To review',
      alerts: 'Open alerts',
      monthHint: '{month}',
      reviewHint: 'logbooks {logbooks} · fuel {fuel} · expenses {expenses}',
      alertsHint: '{critical} critical',
      alertsHintNone: 'none critical',
      vsPrev: 'vs previous month',
      noTrend: 'not enough trend data',
    },
    queue: { title: 'To do', description: 'Review queues and problems that need a response. Click to open the list.' },
    health: {
      title: 'Fleet health',
      description: 'A 0–100 score from deadlines, incidents, lease limits and missing logbooks.',
      good: 'in good shape',
      attention: 'need attention',
      critical: 'critical',
      attentionList: 'Vehicles needing attention',
      empty: 'No vehicle needs attention.',
      noVehicles: 'No active or in-service vehicles — the score is not calculated.',
      rule: 'The score is indicative: it uses the signals visible on the dashboard (incidents — the 5 latest). A vehicle with no signals = 100.',
      more: 'and {n} more',
    },
    today: {
      title: 'Vehicles today',
      description: 'State from open logbooks and vehicle status.',
      all: 'All',
      filter: 'Vehicle state',
      since: 'since {time}',
      planned: 'planned {km}',
      odometer: 'odometer {km}',
      noDriver: 'no driver',
      defaultDriver: 'default driver',
      pendingDecision: 'decision pending',
      showAll: 'Show all ({n})',
      showLess: 'Show less',
      empty: 'No vehicles for the selected filters.',
      emptyState: 'No vehicle is in this state.',
      vehicles: 'Vehicle list',
      barAria: 'On route {a}, in service {b}, parked {c}',
    },
    leases: {
      title: 'Lease limits',
      description: 'Km limit usage and end-of-contract forecast — the most loaded contracts.',
      remaining: '{km} left',
      exceeded: 'exceeded by {km}',
      forecast: 'forecast {percent}',
      indicative: 'indicative',
      daysLeft: '{n} days to contract end',
      empty: 'No active lease contracts.',
      all: 'All contracts',
      limitOf: 'of {km}',
    },
    due: {
      title: 'Deadline timeline',
      description: 'Inspections and insurance within {days} days (by week).',
      overdue: 'Overdue',
      week: 'days {from}–{to}',
      weekNow: 'this week',
      empty: 'No deadlines in this period.',
      emptyBucket: 'No deadlines in the selected week.',
      showAll: 'Show all',
      selected: 'Selected: {label}',
      barAria: '{label}: {n} deadlines',
    },
    feed: {
      title: 'Alerts',
      description: 'The most important open fleet alerts.',
      empty: 'No open alerts — the fleet is in good shape.',
      all: 'All alerts ({n})',
      go: 'Open',
      ack: 'Acknowledge',
    },
    trend: {
      title: 'Costs and mileage',
      description: 'Latest full months (excluding the current, partial one).',
      metric: { km: 'Mileage', cost: 'Costs', costPerKm: 'Cost/km' },
      aria: '{metric} — {from} to {to}',
      latest: 'Last month: {value}',
      empty: 'Not enough data for the chart.',
      link: 'Full analytics',
    },
    details: 'Details',
  },
};
